import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Type } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  ModalController,
  ModalOptions,
  NavController,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { AlertOptions, OverlayEventDetail } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';

import { catchError, map } from 'rxjs/operators';
import { BZCSplittableRichLink } from 'src/app/task-process/shared/models';
import { AIAssistantAnswer } from 'src/app/task-process/shared/models/ai-assistant-answer.model';
import { environment } from 'src/environments/environment';
import { AbstractModalWithResult } from '../components/abstract-modal-with-result';
import { SelectInPopoverOption } from '../components/select-in-popover/select-in-popover-option';
import { SelectInPopoverComponent } from '../components/select-in-popover/select-in-popover.component';
import { SpinnerService } from './spinner.service';

export const API_URL_PREFIX = environment.apiURLPrefix;
export const API_URL_COMPANIES = API_URL_PREFIX + '/api/1.0/companies';
export const API_URL_COMPANIES_V2 = API_URL_PREFIX + '/api/2.0/companies';

export const JSON_CONTENT_TYPE_HEADER = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

// This enum is not in routing-module.ts to prevent circular dependencies
export enum RoutePaths {
  CompanySelection = '/routes/company-selection',
  Login = '/routes/login',
  SamlResult = '/routes/saml-result',
  AuthRoutesPrefix = '/routes/auth',
  TasksListsTabs = '/routes/auth/tasks-lists-tabs',
  TaskProcessPrefix = '/routes/auth/task-process',
  TaskProcessForCrmPrefix = '/routes/auth/task-process-for-crm',
  CulDeSac = '/routes/auth/dead-end',
  PreferencesTab = '/routes/auth/tasks-lists-tabs/preferences',
  PreferencePagesPrefix = '/routes/auth/preferences',
  SettingsPagesPrefix = '/routes/auth/settings',
}

@Injectable({
  providedIn: 'root',
})
export class BasicService {
  public window: Window;
  public appIsVisible$ = new BehaviorSubject<boolean>(false);
  public answerSuggestionPending: {
    [key: number]: {
      resultBS: BehaviorSubject<AIAssistantAnswer>;
      expiredSubscription?: Subscription; // after 60 seconds if any messages were received consider there was a timeout.
      received?: boolean; // In case of success or WS / IA / serveur issue, allows to consider the message was received and allows the user to recall the Ia is needed.
      minimized?: boolean;
    };
  } = {};
  handleBZCSplittableRichLinkBS = new BehaviorSubject<BZCSplittableRichLink>(undefined);
  handleClearBZCSplittableRichLinkAttachmentsBS = new BehaviorSubject<boolean>(undefined);
  handleBZCSplittableRichLinkSubscription = new Subscription();

  private cardModalPresentingElement: HTMLElement;

  constructor(
    private modalController: ModalController,
    private toastController: ToastController,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
    private navController: NavController,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private httpClient: HttpClient,
    @Inject(DOCUMENT) public document: Document
  ) {
    this.window = window;
    // Detect app visibility manually, as ionic doesn't expose it
    const targetNode = this.document.querySelector('html');
    const config = { attributes: true };
    const callback = (mutationsList, observer) => {
      this.checkIfAppIsVisible();
      if (this.appIsVisible$.value) {
        observer.disconnect();
      }
    };
    new MutationObserver(callback).observe(targetNode, config);
    this.checkIfAppIsVisible();
  }

  setCardModalPresentingElement(element: HTMLElement) {
    this.cardModalPresentingElement = element;
  }

  showSpinner() {
    this.spinnerService.showSpinner();
  }

  hideSpinner(options?: { restoreFocusAfterMs: number }) {
    this.spinnerService.hideSpinner(options);
  }

  async toastAlert(messageKey, messageParams?) {
    let message = this.translate(messageKey, messageParams);
    if (messageKey === 'bug/unexpected-error') {
      try {
        // Specific code to have date time in case of unexpected error to help the support team.
        message = `<div>${message}</div><div class="details">${new Date().toISOString()}</div>`;
      } catch {
        console.error('Unable to get the date');
      }
    }

    const toast = await this.toastController.create({
      message,
      color: 'danger',
      icon: 'bug-outline',
      buttons: [
        {
          side: 'end',
          icon: 'close',
        },
      ],
    });
    toast.present();
  }

  async toastInformation(messageKey, messageParams?) {
    const toast = await this.toastController.create({
      message: this.translate(messageKey, messageParams),
      color: 'secondary',
      duration: 3000,
    });
    toast.present();
  }

  async toastSuccess(messageKey: string, messageParams?: { [paramName: string]: string }) {
    const toast = await this.toastController.create({
      message: this.translate(messageKey, messageParams),
      color: 'success',
      duration: 3000,
    });
    toast.present();
  }

  async modal(modalOptions: ModalOptions = {} as ModalOptions, isPreventClose?: boolean) {
    if (isPreventClose) {
      modalOptions.keyboardClose = false;
      modalOptions.backdropDismiss = false;
    }
    const modal = await this.modalController.create(modalOptions);
    modal.present();
    return modal;
  }

  async cardModal(component, componentProps?) {
    const modal = await this.modalController.create({
      component,
      componentProps,
      swipeToClose: true,
      presentingElement: this.cardModalPresentingElement,
    });
    modal.present();
    return modal;
  }

  private async popover(event, component, componentProps?, cssClass?, id?) {
    const popover = await this.popoverController.create({
      component,
      componentProps,
      event,
      cssClass,
      id,
      mode: environment.isNativeApp ? 'ios' : 'md', // use md popover on salesforce widget
    });
    popover.present();
    return popover;
  }

  // if event is null, the popover will be displayed in the center of the viewport (as a modal, in other words)
  async popoverWithResult<T>(
    event,
    componentClass: Type<AbstractModalWithResult<T>>,
    componentProps?,
    extraCssClasses?: string
  ): Promise<OverlayEventDetail<T>> {
    const cssClasses = `alc-select-in-popover ${extraCssClasses || ''}`;
    const id = componentClass['modalId']; // unfortunately "abstract static" is not supported by Typescript
    const popover = await this.popover(event, componentClass, componentProps, cssClasses, id);
    return await popover.onWillDismiss();
  }

  /**
   * Returns a promise that will resolve to the selected element
   */
  async selectInPopover(
    event: MouseEvent,
    elements: SelectInPopoverOption[],
    cssClass?: string
  ): Promise<OverlayEventDetail<any>> {
    // eslint-disable-next-line prefer-const
    let popover: HTMLIonPopoverElement;
    const componentProps = {
      elements,
      dismissFn: element => popover.dismiss(element, 'select'),
    };
    const cssClasses = `alc-select-in-popover ${cssClass || ''}`;
    popover = await this.popover(event, SelectInPopoverComponent, componentProps, cssClasses);
    return popover.onWillDismiss();
  }

  dismissModal(data?: any) {
    this.modalController.dismiss(data);
  }

  async modalAlertWithOK(titleKey, messageKey, messageParams?) {
    const options: AlertOptions = {
      message: this.translate(messageKey, messageParams),
      buttons: [this.translate('m/ok')],
    };
    if (titleKey) {
      options.header = this.translate(titleKey);
    }
    return this.presentAlert(options);
  }

  async presentAlert(options: AlertOptions) {
    const alert = await this.alertController.create(options);
    alert.present();
    return alert.onDidDismiss();
  }

  async actionSheet(config) {
    return (await this.actionSheetController.create(config)).present();
  }

  async navigateForward(route, params?) {
    if (environment.bundledWithAngularJSApp) {
      console.warn(`not navigating to ${route} because we are bundledWithAngularJSApp`);
      return await Promise.resolve(false);
    } else {
      return await this.navController.navigateForward(route, params);
    }
  }

  async navigateBack(route, params?) {
    if (environment.bundledWithAngularJSApp) {
      console.warn(`not navigating to ${route} because we are bundledWithAngularJSApp`);
      return await Promise.resolve(false);
    } else {
      return await this.navController.navigateBack(route, params);
    }
  }

  async navigateRoot(route, params?) {
    if (environment.bundledWithAngularJSApp) {
      console.warn(`not navigating to ${route} because we are bundledWithAngularJSApp`);
      return await Promise.resolve(false);
    } else {
      return this.navController.navigateRoot(route, params);
    }
  }

  fullURLForRoutePath(routePath: string) {
    return `${this.window.location.origin}${environment.baseHref}${routePath}`;
  }

  translate(key: string | Array<string>, params?: { [paramName: string]: string }): string | any {
    // Avoid to crash the applicaiton if the key is null.
    if (!key) {
      return;
    }
    return this.translateService.instant(key, params);
  }

  resetAIAssistantAnswerForTaskId(taskId: number) {
    this.answerSuggestionPending[taskId] = null;
  }

  private checkIfAppIsVisible() {
    if (this.document.querySelector('html.hydrated')) {
      this.appIsVisible$.next(true);
    }
  }

  getUserCountryCode(): Observable<string> {
    return this.httpClient.get('https://ipapi.co/country_code', { responseType: 'text' }).pipe(
      catchError(() => of('us')),
      map(value => value?.toLowerCase() || 'us')
    );
  }
}
